import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Colors } from '../../../themes/colors';
import { MaterialCommunityIconName } from '../../../utils/iconTypes';
import { useFormContext } from '../../forms/formContext';
import { ControlledSelect } from '../../forms/select';

import { useEffect, useState } from 'react';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import {
  ItemType,
  RenderListItemPropsInterface,
} from 'react-native-dropdown-picker';
import { RadioButton } from 'react-native-paper';
import AlertLogicInput from './alertLogicInput';
import { AlertOptions } from './alertOptions';
import { AlertInputs, AlertType } from './types';

type AlertLogicPickerProps = {
  control: Control<any>;
  alertType: AlertType | undefined;
  secondAlertType: AlertType | undefined;
  additionalLogic: boolean;
  isCounterRoutine: boolean;
  counterRoutineItems: any[];
  showRoutinesDropdown: boolean;
  openDropdownOnLoad?: boolean;
  setAdditionalLogic: (value: boolean) => void;
  setShowRoutinesDropdown: (value: boolean) => void;
};

export default function AlertLogicPicker({
  control,
  alertType,
  secondAlertType,
  additionalLogic,
  isCounterRoutine,
  counterRoutineItems,
  showRoutinesDropdown,
  openDropdownOnLoad,
  setShowRoutinesDropdown,
  setAdditionalLogic,
}: AlertLogicPickerProps) {
  const { t } = useTranslation();
  const { setValue, getValues } = useFormContext();
  const [uomLabel, setUomLabel] = useState('');

  const intervalRoutineId = getValues(AlertInputs.intervalRoutineId);

  useEffect(() => {
    if (!counterRoutineItems || counterRoutineItems.length === 0) return;

    const routineMatch = counterRoutineItems.find(
      (item) => item.value === intervalRoutineId
    );
    setUomLabel(routineMatch?.uom || '');
  }, [intervalRoutineId, counterRoutineItems]);

  const defaultDropdownItems = [
    {
      label: t('alerts.onDate'),
      value: AlertType.Date,
    }, {
      label: t('alerts.onRecurringInterval'),
      value: AlertType.RecurringInterval,
    }, {
      label: t('alerts.onDayInterval'),
      value: AlertType.DayInterval,
    }, { 
      label: t('alerts.onInterval'), 
      value: AlertType.Interval 
    }, { 
      label: t('alerts.onDays'), 
      value: AlertType.Days 
    }, {
      label: t('alerts.onDaysSkipped'),
      value: AlertType.DaysSkipped,
    }, {
      label: t('alerts.onRoutineCompletions'),
      value: AlertType.RoutineCompletions,
    },
  ];

  const counterDropdownItems = [
    {
      label: t('alerts.onDate'),
      value: AlertType.Date,
    }, {
      label: t('alerts.onRecurringInterval'),
      value: AlertType.RecurringInterval,
    }, {
      label: t('alerts.onInterval'), 
      value: AlertType.Interval 
    }, { 
      label: t('alerts.onDays'), 
      value: AlertType.Days 
    }, { 
      label: t('alerts.minDropdownPlaceholder'), 
      value: AlertType.Min 
    }, { 
      label: t('alerts.maxDropdownPlaceholder'), 
      value: AlertType.Max 
    }, {
      label: t('alerts.onDaysSkipped'),
      value: AlertType.DaysSkipped,
    }, {
      label: t('alerts.onRoutineCompletions'),
      value: AlertType.RoutineCompletions,
    },
  ];

  const AlertItem = <T,>({
    label,
    value,
    isSelected,
    onPress,
    disabled,
    item,
    parent,
  }: RenderListItemPropsInterface<T>) => {
    const handlePress = () => {
      if (item.value === AlertType.Interval) {
        setShowRoutinesDropdown(true);
      }

      if (item.type === AlertInputs.onIntervalStart) {
        setValue(AlertInputs.onIntervalStart, item.count?.toString() || '', {
          shouldDirty: true,
          shouldValidate: true,
        });
        setUomLabel(item.uom);
      }

      onPress(item as ItemType<T>);
    };

    const radioButtonValue = (isSelected && 'checked') || 'unchecked';

    return (
      <Pressable
        onPress={handlePress}
        style={styles.listItem}
      >
        <Text style={styles.itemLabel}>{label}</Text>
        <RadioButton.Android
          value="test"
          status={radioButtonValue}
          uncheckedColor={Colors.grayDivider}
          hitSlop={10}
          onPress={handlePress}
        />
      </Pressable>
    );
  };

  const chevronIcon = (name: MaterialCommunityIconName) => {
    return <MaterialCommunityIcons name={name} size={28} />;
  };

  const renderLogicInput = (
    dropdownName: string,
    selectedAlertType: AlertType | undefined,
    otherSelectedAlertType: AlertType | undefined,
    isPrimaryAlertInput: boolean
  ) => {
    const dropdownItems = isCounterRoutine
      ? counterDropdownItems
      : defaultDropdownItems;

    const filteredItems = dropdownItems.filter(
      (item) => item.value !== otherSelectedAlertType
    );

    const DROPDOWN_MAX_HEIGHT = 350;

    return (
      <>
        <ControlledSelect
          name={dropdownName}
          control={control}
          label={t('alerts.setAlertLogicPlaceholder')}
          items={filteredItems}
          listMode="SCROLLVIEW"
          dropDownPickerProps={{
            scrollViewProps: {
              nestedScrollEnabled: true,
            },
            style: styles.dropdown,
            textStyle: styles.itemLabel,
            placeholderStyle: styles.placeholderLabel,
            dropDownContainerStyle: [
              styles.dropdownListContainer,
              { minHeight: 'auto' },
              { maxHeight: DROPDOWN_MAX_HEIGHT }
            ],
            openDropdownOnLoad: openDropdownOnLoad,
            ArrowDownIconComponent: () => chevronIcon('chevron-down'),
            ArrowUpIconComponent: () => chevronIcon('chevron-up'),
            renderListItem: (props) => <AlertItem {...props} />,
          }}
        />
        {selectedAlertType === AlertType.Interval &&
          counterRoutineItems &&
          showRoutinesDropdown && (
            <ControlledSelect
              name={AlertInputs.intervalRoutineId}
              control={control}
              label={t('alerts.selectRoutine')}
              items={counterRoutineItems}
              listMode="SCROLLVIEW"
              dropDownPickerProps={{
                scrollViewProps: {
                  nestedScrollEnabled: true,
                },
                style: [styles.dropdown, { marginTop: 8 }],
                textStyle: styles.itemLabel,
                placeholderStyle: styles.placeholderLabel,
                dropDownContainerStyle: [
                  styles.dropdownListContainer,
                  { minHeight: 'auto' },
                  { maxHeight: DROPDOWN_MAX_HEIGHT }
                ],
                openDropdownOnLoad: openDropdownOnLoad,
                ArrowDownIconComponent: () => chevronIcon('chevron-down'),
                ArrowUpIconComponent: () => chevronIcon('chevron-up'),
                renderListItem: (props) => <AlertItem {...props} />,
              }}
            />
          )}
        {selectedAlertType && counterRoutineItems && (
          <AlertLogicInput
            type={selectedAlertType}
            isPrimaryAlertInput={isPrimaryAlertInput}
            uomLabel={uomLabel || 'value'}
            setAdditionalLogic={(value) => setAdditionalLogic(value)}
          />
        )}
      </>
    );
  };

  useEffect(() => {
    if (!secondAlertType) return;

    setAdditionalLogic(true);
  }, [secondAlertType]);

  return (
    <>
      <View style={[styles.sectionContainer]}>
        <Text style={styles.title}>{t('alerts.setAlertLogic')}</Text>
        {renderLogicInput('alertType', alertType, secondAlertType, true)}

        {alertType && !additionalLogic && (
          <Pressable
            style={styles.addAlertButton}
            hitSlop={5}
            onPress={() => setAdditionalLogic(true)}
          >
            <MaterialCommunityIcons
              name="plus"
              style={[styles.addAnotherLabel, { fontSize: 18 }]}
            />
            <Text style={styles.addAnotherLabel}>{t('alerts.addAnother')}</Text>
          </Pressable>
        )}

        {additionalLogic && (
          <View style={{ marginTop: 15 }}>
            {renderLogicInput(
              'secondAlertType',
              secondAlertType,
              alertType,
              false
            )}
          </View>
        )}
      </View>

      {alertType && (
        <>
          <View
            style={[
              styles.optionalSettingsContainer,
              styles.hideBehindDropdown,
            ]}
          >
            <View style={styles.lineBreak} />
            <Text style={styles.optionalSettingsLabel}>
              {t('alerts.optionalSettings')}
            </Text>
            <View style={styles.lineBreak} />
          </View>
          <View style={[styles.sectionContainer, styles.hideBehindDropdown]}>
            <AlertOptions />
          </View>
        </>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  sectionContainer: {
    paddingHorizontal: 16,
    paddingVertical: 20,
    backgroundColor: Colors.white,
    borderRadius: 8,
  },
  title: {
    color: Colors.blackText,
    fontSize: 16,
    fontWeight: '600',
    letterSpacing: -0.25,
    marginBottom: 12,
  },
  dropdown: {
    borderWidth: 1,
    borderColor: Colors.grayText,
    height: 52,
    fontSize: 16,
    fontWeight: '400',
  },
  placeholderLabel: {
    fontSize: 16,
    fontWeight: '400',
    color: Colors.grayFill,
  },
  dropdownListContainer: {
    flexGrow: 1,
    borderWidth: 1,
    borderColor: Colors.grayDivider,
    backgroundColor: Colors.secondaryGrayHeader,
    marginTop: 8,
    borderTopStartRadius: 8,
    borderTopEndRadius: 8,
    paddingVertical: 20,
    paddingHorizontal: 12,
    position: 'relative',
    top: 0,
    zIndex: 1000,
    elevation: 5,
  },
  listItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 52,
    paddingHorizontal: 12,
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderColor: Colors.primaryGrayHeader,
    marginBottom: 12,
    borderRadius: 8,
  },
  itemLabel: {
    flex: 1,
    fontSize: 16,
    fontWeight: '400',
    color: Colors.blackText,
  },
  addAlertButton: {
    flexDirection: 'row',
    marginTop: 15,
    alignItems: 'center',
    columnGap: 5,
    maxWidth: '35%',
    zIndex: -1,
  },
  addAnotherLabel: {
    color: Colors.primaryDark,
    fontWeight: '500',
    fontSize: 14,
    letterSpacing: -0.25,
  },
  optionalSettingsContainer: {
    height: 30,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 20,
  },
  lineBreak: {
    borderWidth: 0.5,
    maxHeight: 1,
    borderColor: Colors.grayDivider,
    flex: 1,
  },
  optionalSettingsLabel: {
    fontSize: 14,
    fontWeight: '400',
    letterSpacing: -0.2,
    color: Colors.grayText,
    marginHorizontal: 10,
  },
  hideBehindDropdown: {
    zIndex: -1,
  },
});
